/**
 * LabelManagerDialog — create / rename / recolor / delete an account's labels
 * (the account-scoped, many-per-message tags). Owns its own CRUD via the label
 * mutation hooks. Unlike categories (holder-scoped), labels belong to one
 * account, so this dialog is passed the `accountId` it manages. Deleting a label
 * removes it from every message that carried it (join rows cascade).
 */

import { useState, type FormEvent } from 'react';
import { Trash2, Plus, Loader2, Check, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useEmailConnections } from '../hooks/useConnectAccount';
import { DialogAccountPicker } from './DialogAccountPicker';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import type { EmailLabelMailboxDto } from '@/lib/api';
import {
  useEmailLabels,
  useCreateEmailLabel,
  useUpdateEmailLabel,
  useDeleteEmailLabel,
} from '../hooks/useMailbox';

const DEFAULT_NEW_COLOR = '#6B7280';

/** One editable label row: recolor (native picker) + rename-on-commit + delete. */
function LabelRow({ label, accountId }: { label: EmailLabelMailboxDto; accountId: string }) {
  const updateLabel = useUpdateEmailLabel();
  const deleteLabel = useDeleteEmailLabel();
  const [name, setName] = useState(label.name);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const trimmed = name.trim();
  const dirty = trimmed.length > 0 && trimmed !== label.name;
  const commitName = () => {
    if (dirty) updateLabel.mutate({ labelId: label.labelId, accountId, name: trimmed });
    else setName(label.name); // snap back an empty/unchanged edit
  };

  return (
    <div className="flex items-center gap-2 rounded-md border px-2 py-1.5">
      <input
        type="color"
        aria-label={`Colour for ${label.name}`}
        title="Change colour"
        value={label.color}
        onChange={(e) =>
          updateLabel.mutate({ labelId: label.labelId, accountId, color: e.target.value })
        }
        className="h-6 w-6 shrink-0 cursor-pointer rounded border bg-transparent p-0"
      />
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={commitName}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          } else if (e.key === 'Escape') {
            setName(label.name);
          }
        }}
        maxLength={60}
        aria-label={`Label name (${label.name})`}
        className="h-7 flex-1 text-sm"
      />
      {confirmDelete ? (
        <span className="flex items-center gap-1">
          <span className="text-[11px] text-muted-foreground">Delete?</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-destructive"
            aria-label={`Confirm delete ${label.name}`}
            disabled={deleteLabel.isPending}
            onClick={() => deleteLabel.mutate({ labelId: label.labelId, accountId })}
          >
            {deleteLabel.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            aria-label="Cancel delete"
            onClick={() => setConfirmDelete(false)}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </span>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 shrink-0 p-0 text-muted-foreground hover:text-destructive"
          aria-label={`Delete ${label.name}`}
          onClick={() => setConfirmDelete(true)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

export interface LabelManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The account whose labels this dialog manages. */
  accountId: string | undefined;
}

export function LabelManagerDialog({ open, onOpenChange, accountId }: LabelManagerDialogProps) {
  const { data: labels, isLoading } = useEmailLabels(accountId, open);
  const createLabel = useCreateEmailLabel();
  const { data: connections } = useEmailConnections();
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(DEFAULT_NEW_COLOR);

  const trimmedNew = newName.trim();
  const addLabel = (e: FormEvent) => {
    e.preventDefault();
    if (!trimmedNew || !accountId) return;
    createLabel.mutate(
      { accountId, name: trimmedNew, color: newColor },
      {
        onSuccess: () => {
          setNewName('');
          setNewColor(DEFAULT_NEW_COLOR);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Manage labels</DialogTitle>
          {/* Follows the mailbox open in the main view — say which one. */}
          <DialogAccountPicker accounts={connections ?? []} value={accountId} readOnly />
          <DialogDescription>
            Labels tag individual messages (many per message). Deleting one removes it from every
            message that had it.
          </DialogDescription>
        </DialogHeader>

        <div className="my-2 flex max-h-[45vh] flex-col gap-1.5 overflow-y-auto">
          {!accountId ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Select an account to manage its labels.
            </p>
          ) : isLoading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading labels…
            </div>
          ) : !labels || labels.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No labels yet — add one below.
            </p>
          ) : (
            labels.map((l) => <LabelRow key={l.labelId} label={l} accountId={accountId} />)
          )}
        </div>

        {/* Add a new label. */}
        <form onSubmit={addLabel} className="flex items-center gap-2 border-t pt-3">
          <input
            type="color"
            aria-label="New label colour"
            title="Pick a colour"
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
            className="h-8 w-8 shrink-0 cursor-pointer rounded border bg-transparent p-0"
          />
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New label name"
            maxLength={60}
            aria-label="New label name"
            className="h-8 flex-1"
          />
          <Button
            type="submit"
            size="sm"
            className="h-8 gap-1"
            disabled={!trimmedNew || !accountId || createLabel.isPending}
          >
            {createLabel.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
            Add
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
